import { Add, Close, DragIndicator, Info } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { ApprovalMode, User } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import {
  autoApproveAlertContentStyle,
  autoApproveAlertStyle,
  approverChipContainerStyle,
  approverChipStyle,
  approverRowInnerBoxStyle,
  approverRowStyle,
  defaultModeButtonColors,
  defaultModeButtonStyles,
  modeButtonsContainerStyle,
  noApproversPlaceholderStyle,
  parallelContainerStyle,
  requiredApprovalsBoxStyle,
  ruleFormHeaderStyle,
  ruleStepIndicatorStyle,
  sequentialContainerStyle,
  approversListTitleStyle,
  fallbackStepIndicatorStyle
} from 'styles/pages/ApprovalRuleFormStyle';

export interface ApprovalModeSectionProps {
  selectedMode: ApprovalMode;
  setSelectedMode: (mode: ApprovalMode) => void;
  selectedApprovers: User[];
  setSelectedApprovers: (approvers: User[]) => void;
  requiredApprovals: number;
  setRequiredApprovals: (approvals: number) => void;
  approvers?: User[];
  approversError?: string;
  onClearApproversError?: () => void;
  isFallback?: boolean;
  stepNumber?: number;
}

export const AvailableUsers = ({
  availableUsers,
  onAddUser,
  placeholder,
  showAddIcon = false,
  error = '',
  onClearError
}: {
  availableUsers: User[];
  onAddUser: (user: User) => void;
  placeholder: string;
  showAddIcon?: boolean;
  error?: string;
  onClearError?: () => void;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [resetKey, setResetKey] = useState(0);

  return (
    <Box>
      <Autocomplete
        key={resetKey}
        options={availableUsers}
        getOptionLabel={(option) => option.name}
        value={undefined}
        inputValue={inputValue}
        onInputChange={(_, value) => {
          setInputValue(value);
          onClearError?.();
        }}
        onChange={(_, value) => {
          if (value) {
            onAddUser(value);
            setInputValue('');
            setResetKey((prev) => prev + 1);
          }
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              placeholder={placeholder}
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: showAddIcon ? (
                    <InputAdornment position="start">
                      <Add sx={{ color: '#9ca3af', fontSize: '0.75rem' }} />
                    </InputAdornment>
                  ) : undefined
                },
                htmlInput: {
                  ...params.inputProps
                }
              }}
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  paddingLeft: showAddIcon ? '0.5rem' : undefined
                }
              }}
            />
            {error && (
              <Typography
                variant="body2"
                sx={{
                  color: '#d32f2f',
                  mt: '0.5rem',
                  textAlign: 'left',
                  fontSize: '0.75rem'
                }}
              >
                {error}
              </Typography>
            )}
          </>
        )}
        freeSolo={false}
        clearOnBlur
        size="small"
        disableClearable
      />
    </Box>
  );
};

export const ApprovalModeSection = ({
  selectedMode,
  setSelectedMode,
  selectedApprovers,
  setSelectedApprovers,
  requiredApprovals,
  setRequiredApprovals,
  approvers = [],
  approversError = '',
  onClearApproversError,
  isFallback = false,
  stepNumber = 3
}: ApprovalModeSectionProps) => {
  const { translate } = useTranslations();

  const showAutoApprove = !isFallback;

  const prevModeRef = useRef<ApprovalMode | undefined>(undefined);
  const isSettledRef = useRef(false);

  useEffect(() => {
    if (!isSettledRef.current) {
      if (selectedApprovers.length > 0) {
        isSettledRef.current = true;
        prevModeRef.current = selectedMode;
      }
      return;
    }

    if (selectedMode !== prevModeRef.current) {
      setSelectedApprovers([]);
      setRequiredApprovals(1);
    }

    prevModeRef.current = selectedMode;
  }, [
    selectedMode,
    selectedApprovers.length,
    setSelectedApprovers,
    setRequiredApprovals
  ]);

  const availableUsers = approvers.filter(
    (user) => !selectedApprovers.some((a) => a.id === user.id)
  );

  const handleAddApprover = (appr: User) => {
    if (!selectedApprovers.some((a) => a.id === appr.id)) {
      setSelectedApprovers([...selectedApprovers, appr]);
      onClearApproversError?.();
    }
  };

  const handleRemoveApprover = (id: number) => {
    setSelectedApprovers(selectedApprovers.filter((a) => a.id !== id));
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    _index: number
  ) => {
    e.dataTransfer.setData('text/plain', _index.toString());
    e.currentTarget.dataset.draggedIndex = _index.toString();
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    _index: number
  ) => {
    e.preventDefault();
    e.currentTarget.dataset.dragOverIndex = _index.toString();
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetIndex: number
  ) => {
    e.preventDefault();
    const sourceIndex = Number.parseInt(
      e.dataTransfer.getData('text/plain'),
      10
    );

    if (sourceIndex === targetIndex) return;

    const newApprovers = [...selectedApprovers];
    const [movedItem] = newApprovers.splice(sourceIndex, 1);
    newApprovers.splice(targetIndex, 0, movedItem);

    setSelectedApprovers(newApprovers);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '1rem',
        border: '1px solid #e5e7eb'
      }}
    >
      <Box sx={ruleFormHeaderStyle}>
        <Box
          sx={{
            ...(isFallback
              ? fallbackStepIndicatorStyle
              : ruleStepIndicatorStyle)
          }}
        >
          {stepNumber}
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600
          }}
        >
          {translate('approvals.approvalModeApprovers')}
        </Typography>
      </Box>
      <Box sx={modeButtonsContainerStyle}>
        {showAutoApprove && (
          <Button
            size="small"
            variant={
              selectedMode === ApprovalMode.AUTO_APPROVE
                ? 'contained'
                : 'outlined'
            }
            onClick={() => setSelectedMode(ApprovalMode.AUTO_APPROVE)}
            sx={{
              ...defaultModeButtonStyles,
              ...(selectedMode === ApprovalMode.AUTO_APPROVE
                ? {
                    backgroundColor: '#f0fdf4',
                    color: '#15803d',
                    fontWeight: 600,
                    border: '1px solid transparent'
                  }
                : defaultModeButtonColors)
            }}
          >
            {translate('approvals.autoApprove')}
          </Button>
        )}
        <Button
          size="small"
          variant={
            selectedMode === ApprovalMode.SEQUENTIAL ? 'contained' : 'outlined'
          }
          onClick={() => setSelectedMode(ApprovalMode.SEQUENTIAL)}
          sx={{
            ...defaultModeButtonStyles,
            ...(selectedMode === ApprovalMode.SEQUENTIAL
              ? {
                  backgroundColor: '#eff6ff',
                  color: '#1e40af',
                  fontWeight: 600,
                  border: '1px solid transparent'
                }
              : defaultModeButtonColors)
          }}
        >
          {translate('approvals.sequential')}
        </Button>
        <Button
          size="small"
          variant={
            selectedMode === ApprovalMode.PARALLEL ? 'contained' : 'outlined'
          }
          onClick={() => setSelectedMode(ApprovalMode.PARALLEL)}
          sx={{
            ...defaultModeButtonStyles,
            ...(selectedMode === ApprovalMode.PARALLEL
              ? {
                  backgroundColor: '#fef9e7',
                  color: '#b45309',
                  fontWeight: 600,
                  border: '1px solid transparent'
                }
              : defaultModeButtonColors)
          }}
        >
          {translate('approvals.parallel')}
        </Button>
      </Box>
      {selectedMode === ApprovalMode.SEQUENTIAL && (
        <Box sx={sequentialContainerStyle}>
          <Typography
            variant="body2"
            sx={{
              ...approversListTitleStyle,
              variant: 'body2'
            }}
          >
            {translate('approvals.approversOrdered')}
          </Typography>
          <Box sx={{ marginBottom: '0.75rem' }}>
            {selectedApprovers.map((appr, index) => {
              const isDragOver =
                'dragOverIndex' in appr &&
                Number.parseInt(String(appr.dragOverIndex)) === index;
              return (
                <Box
                  key={appr.id}
                  sx={{
                    ...approverRowStyle,
                    border: isDragOver
                      ? '1px solid #078DEE'
                      : '1px solid #e5e7eb'
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                >
                  <Box sx={approverRowInnerBoxStyle}>
                    <DragIndicator
                      sx={{ color: '#d1d5db', fontSize: '1rem' }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#9ca3af',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        minWidth: '16px',
                        textAlign: 'center'
                      }}
                    >
                      {index + 1}.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.875rem', color: '#374151' }}
                    >
                      {appr.name}
                    </Typography>
                  </Box>
                  <Close
                    sx={{
                      color: '#d1d5db',
                      cursor: 'pointer',
                      '&:hover': { color: '#ef4444' }
                    }}
                    onClick={() => handleRemoveApprover(appr.id)}
                  />
                </Box>
              );
            })}
          </Box>
          <AvailableUsers
            availableUsers={availableUsers}
            onAddUser={handleAddApprover}
            placeholder={translate('approvals.addApprover')}
            showAddIcon={true}
            error={approversError}
            onClearError={onClearApproversError}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.75rem'
            }}
          >
            <DragIndicator sx={{ color: '#9ca3af' }} />
            <Typography
              variant="body2"
              sx={{ color: '#9ca3af', fontSize: '0.875rem' }}
            >
              {translate('approvals.dragToReorder')}
            </Typography>
          </Box>
        </Box>
      )}
      {showAutoApprove && selectedMode === ApprovalMode.AUTO_APPROVE && (
        <Box sx={autoApproveAlertStyle}>
          <Box sx={autoApproveAlertContentStyle}>
            <Info />
            <Typography variant="body2" sx={{ margin: '0.75rem 0' }}>
              {translate('approvals.autoApproveHelp')}
            </Typography>
          </Box>
        </Box>
      )}
      {selectedMode === ApprovalMode.PARALLEL && (
        <Box sx={parallelContainerStyle}>
          <Typography
            variant="body1"
            sx={{
              marginBottom: '0.375rem',
              textAlign: 'left',
              fontSize: '0.875rem',
              fontWeight: 600
            }}
          >
            {translate('approvals.approvers')}
          </Typography>
          <Box sx={approverChipContainerStyle}>
            {selectedApprovers.map((appr) => (
              <Chip
                key={appr.id}
                label={appr.name}
                deleteIcon={<Close />}
                onDelete={() => handleRemoveApprover(appr.id)}
                size="small"
                sx={approverChipStyle}
              />
            ))}
            {selectedApprovers.length === 0 && (
              <Typography variant="body2" sx={noApproversPlaceholderStyle}>
                {translate('approvals.noApproversSelected')}
              </Typography>
            )}
          </Box>
          <AvailableUsers
            availableUsers={availableUsers}
            onAddUser={handleAddApprover}
            placeholder={translate('approvals.addApprover')}
            error={approversError}
            onClearError={onClearApproversError}
          />
          {selectedApprovers.length > 0 && (
            <Box sx={requiredApprovalsBoxStyle}>
              <Typography variant="body1">
                {translate('approvals.requiredApprovals')}
              </Typography>
              <TextField
                type="number"
                slotProps={{
                  htmlInput: {
                    min: 1,
                    max: selectedApprovers.length
                  }
                }}
                value={requiredApprovals}
                onChange={(e) => {
                  const val = Number.parseInt(e.target.value, 10);
                  if (val >= 1 && val <= selectedApprovers.length) {
                    setRequiredApprovals(val);
                  }
                }}
                size="small"
              />
              <Typography variant="body1">
                {translate('labels.of')} {selectedApprovers.length}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};
